import { toJS } from 'mobx'

import { GetCollectionRequestParamsTask } from '@/Core/Tasks/Resources/GetCollectionRequestParamsTask'
import { DownloadFileTask } from '@/Ship/Tasks/DownloadFileTask'

type Params = Record<string, unknown>
type Transformer = (params: Params, listContext: unknown) => Params

/**
 * Мінімум, який вивантаження знає про ядровий `ListStore`: з чого будується
 * запит таблиці. Описано вузько навмисно — повний контракт стора живе в `Core`.
 */
export interface ExportListContextLike {
    restUrl?: string
    fullFilter?: Params
    sorter?: Params
    search?: string
    props?: { searchParamsTransformer?: Transformer }
}

/**
 * Параметри вивантаження — **ті самі**, що в запиті таблиці: фільтри (разом із
 * префільтром сторінки), пошук і сортування, зібрані тим самим трансформером
 * списку (`searchParamsTransformer`, як у `LoadActionTask` ядра). Без
 * сторінки: файл — уся вибірка, а не поточна сторінка.
 */
export const exportParamsFor = (listContext: ExportListContextLike): Record<string, string | number | boolean> => {
    const transformer: Transformer =
        typeof listContext.props?.searchParamsTransformer === 'function'
            ? listContext.props.searchParamsTransformer
            : (GetCollectionRequestParamsTask as unknown as Transformer)

    const params = transformer(
        {
            sorter: toJS(listContext.sorter) ?? {},
            filter: toJS(listContext.fullFilter) ?? {},
            pagination: {},
            ...(listContext.search ? { search: listContext.search } : {}),
        },
        listContext
    )

    // Сторінка й права списку вивантаженню ні до чого
    delete params['page']
    delete params['limit']
    delete params['itemsPerPage']
    delete params['permissions']

    const result: Record<string, string | number | boolean> = {}

    for (const [key, value] of Object.entries(params)) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') result[key] = value
    }

    return result
}

/**
 * «Експорт в Excel» реєстру CRM (`SPEC-008` §5.9): `GET <restUrl>/export` з
 * поточною вибіркою таблиці; файл зберігається під назвою, яку бачив
 * користувач легасі («Клієнти.xlsx», `ExportToExcelTask.js:35`).
 */
export const ExportCrmListTask = async (listContext: ExportListContextLike, fileName: string): Promise<void> => {
    const restUrl = listContext.restUrl

    if (!restUrl) throw new Error('Реєстр не має адреси — вивантажувати нічого.')

    await DownloadFileTask({
        url: `${restUrl}/export`,
        searchParams: exportParamsFor(listContext),
        fileName,
    })
}
